import { cancelAnimation, withSpring, withTiming } from "@-/com.npmjs.react-native-reanimated";
import type { LearnCardSlideViews } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

import { LEARN_DECK_EXIT_DURATION_MS } from "#learn-deck-springs";
import { LEARN_DECK_RECOVERY_SPRING } from "@-/dev.improve.haniverse.lib.learn.deck";

/**
 * Where the view that last left the top is now, or `null` once it is gone: it flies from where it
 * was let go to one body width off its side.
 *
 * Keep worklets above every worklet that calls them. The worklets transform replaces top-level
 * function declarations with eager closure initializers, so source-level hoisting does not survive
 * in the UI runtime.
 */
export function leavingSlideViewOffset(views: LearnCardSlideViews): number | null {
  "worklet";
  const leaving = views.leaving.get();
  const progress = views.leavingProgress.get();
  if (leaving === null || progress >= 1) {
    return null;
  }
  const toX = leaving.direction * views.width.get();
  return leaving.fromX + (toX - leaving.fromX) * progress;
}

/** Continuous slideshow position, shared by the bordered slides and their dots. */
export function slideViewPosition(views: LearnCardSlideViews): number {
  "worklet";
  const width = views.width.get();
  if (width <= 0) {
    return views.shown.get();
  }
  const leavingX = leavingSlideViewOffset(views);
  const leaving = views.leaving.get();
  return leavingX !== null && leaving !== null
    ? leaving.page - leavingX / width
    : views.shown.get() - views.offsetX.get() / width;
}

/**
 * Send a slide card's top view off the stack sideways (`direction` -1 left, 1 right), uncovering the
 * view under it. A swipe continues from where the finger let go.
 *
 * The view underneath is on top at once (`shown`), so the next swipe, Next or Undo acts on it
 * immediately; the leaving view only flies off along its own progress (`leaving`). Nothing waits
 * for the flight to finish: a completion callback that never arrives (a cancelled animation) can
 * no longer leave the slide's paging dead.
 */
export function exitSlideView(views: LearnCardSlideViews, direction: -1 | 1): void {
  "worklet";
  const { exits, incoming, leaving, leavingProgress, offsetX, shown } = views;
  const page = shown.get();
  const next = incoming.get();
  const fromX = (page - slideViewPosition(views)) * views.width.get();
  exits.set({ ...exits.get(), [page]: direction });
  cancelAnimation(offsetX);
  leaving.set({ direction, fromX, page });
  leavingProgress.set(0);
  // The view never tilts, so one body width clears it completely.
  leavingProgress.set(withTiming(1, { duration: LEARN_DECK_EXIT_DURATION_MS }));
  shown.set(next);
  incoming.set(next);
  offsetX.set(0);
}

/** Turn to the view after the top one, sending the top view off to the left (Next, Redo). */
export function advanceSlideView(views: LearnCardSlideViews, page: number): void {
  "worklet";
  // A repeated press for a view already on top (React has not caught up yet) turns nothing.
  if (views.shown.get() >= page) {
    return;
  }
  views.incoming.set(page);
  exitSlideView(views, -1);
}

/**
 * Bring the previous slide into the slideshow from the left (Undo).
 */
export function returnSlideView(views: LearnCardSlideViews, page: number): void {
  "worklet";
  const { incoming, leaving, leavingProgress, offsetX, shown, width } = views;
  if (shown.get() <= page) {
    return;
  }
  const fromX = (page - slideViewPosition(views)) * width.get();
  // The returning view takes over from any view still flying off.
  cancelAnimation(leavingProgress);
  leaving.set(null);
  leavingProgress.set(0);
  incoming.set(shown.get());
  shown.set(page);
  offsetX.set(fromX);
  offsetX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
}

/** Show `page` at once, with nothing moving (a re-cut slide with fewer views). */
export function placeSlideView(views: LearnCardSlideViews, page: number): void {
  "worklet";
  cancelAnimation(views.leavingProgress);
  cancelAnimation(views.offsetX);
  views.leaving.set(null);
  views.leavingProgress.set(0);
  views.shown.set(page);
  views.incoming.set(page);
  views.offsetX.set(0);
}

/**
 * How far the top view has left, 0 (at rest) to 1 (gone): the view underneath fades and grows in
 * by it, and the dots move by it. While a view flies off, that is its flight; otherwise the drag
 * (or the return) of the view on top.
 */
export function slideViewProgress(views: LearnCardSlideViews): number {
  "worklet";
  const width = views.width.get();
  if (width <= 0) {
    return 0;
  }
  const leavingX = leavingSlideViewOffset(views);
  return Math.min(1, Math.abs(leavingX ?? views.offsetX.get()) / width);
}
